import { CloudIntegrationCallbackPage } from '../pages/CloudIntegrationCallbackPage';
import { parseCloudRedirectResult } from '../utils/cloudIntegrationRedirect';
import { MobileAiAssistantPage } from '../features/ai/MobileAiAssistantPage';
import React, { useCallback, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AthleteProvider } from '../context/AthleteContext';
import { NotificationCountProvider } from '../context/NotificationCountProvider';
import { ComparisonNavigationProvider } from '../context/ComparisonNavigationContext';
import { PwaInstallProvider } from '../context/PwaInstallContext';
import { MobileShell } from '../shell/MobileShell';
import { SplashPage } from '../pages/auth/SplashPage';
import { AuthPage } from '../pages/auth/AuthPage';
import { GarminCallbackPage } from '../pages/GarminCallbackPage';
import { HomePage } from '../features/home/HomePage';
import { RecordsPage } from '../features/records/RecordsPage';
import { MobileChartsPage } from '../features/charts/MobileChartsPage';
import { MobileAtpPage } from '../features/atp/MobileAtpPage';
import { MobileNotificationPage } from '../features/notifications/MobileNotificationPage';
import { MobileLibraryPage } from '../features/library/MobileLibraryPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { MobileComparisonPage } from '../features/comparison/MobileComparisonPage';
import { MobileBatchComparePage } from '../features/batch-compare/MobileBatchComparePage';
import type { ComparisonPrefill } from '../pages/comparison/types';
import {
  CalendarLayout,
  NewEventRoute,
  NewMetricRoute,
  NewNoteRoute,
  NewWorkoutRoute,
} from '../features/calendar/CalendarFormRoutes';
import { NmLoadingOverlay } from '../components/NmLoadingOverlay';

function ProtectedRoutes() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const cloudReturn = parseCloudRedirectResult(searchParams);
  const [comparisonPrefill, setComparisonPrefill] = useState<ComparisonPrefill | null>(null);

  const urlComparisonPrefill = useMemo((): ComparisonPrefill | null => {
    const workoutA = searchParams.get('workoutA');
    const workoutB = searchParams.get('workoutB');
    if (!workoutA && !workoutB) return null;
    const userA = searchParams.get('userA');
    const userB = searchParams.get('userB');
    return {
      workoutAId: workoutA ? Number(workoutA) : undefined,
      workoutBId: workoutB ? Number(workoutB) : undefined,
      workoutAUserId: userA ? Number(userA) : undefined,
      workoutBUserId: userB ? Number(userB) : undefined,
    };
  }, [searchParams]);

  const effectiveComparisonPrefill = comparisonPrefill ?? urlComparisonPrefill;

  const consumeComparisonPrefill = useCallback(() => {
    setComparisonPrefill(null);
    if (!searchParams.get('workoutA') && !searchParams.get('workoutB')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('workoutA');
    next.delete('workoutB');
    next.delete('userA');
    next.delete('userB');
    const qs = next.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [searchParams, navigate, location.pathname]);

  const openComparison = useCallback(
    (prefill: ComparisonPrefill) => {
      setComparisonPrefill(prefill);
      const params = new URLSearchParams();
      if (prefill.workoutAId != null) params.set('workoutA', String(prefill.workoutAId));
      if (prefill.workoutBId != null) params.set('workoutB', String(prefill.workoutBId));
      if (prefill.workoutAUserId != null) params.set('userA', String(prefill.workoutAUserId));
      if (prefill.workoutBUserId != null) params.set('userB', String(prefill.workoutBUserId));
      const qs = params.toString();
      navigate(`/comparison${qs ? `?${qs}` : ''}`);
    },
    [navigate],
  );

  if (auth.isLoading) {
    return <NmLoadingOverlay />;
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isCoach = auth.user?.role === 'coach';

  return (
    <PwaInstallProvider enabled>
    <AthleteProvider user={auth.user}>
      <NotificationCountProvider isCoach={isCoach}>
      <ComparisonNavigationProvider value={{ openComparison }}>
        {cloudReturn ? <CloudIntegrationCallbackPage {...cloudReturn} /> : <Routes>
          <Route element={<MobileShell />}>
            <Route index element={<Navigate to="/calendar" replace />} />
            <Route path="calendar" element={<CalendarLayout />}>
              <Route index />
              <Route path="workout/new" element={<NewWorkoutRoute />} />
              <Route path="event/new" element={<NewEventRoute />} />
              <Route path="metric/new" element={<NewMetricRoute />} />
              <Route path="note/new" element={<NewNoteRoute />} />
            </Route>
            <Route path="home" element={<HomePage />} />
            <Route path="charts" element={<MobileChartsPage />} />
            <Route path="batch-compare" element={<MobileBatchComparePage />} />
            <Route path="records" element={<RecordsPage />} />
            <Route path="atp" element={<MobileAtpPage />} />
            <Route path="library" element={<MobileLibraryPage />} />
            <Route path="add" element={<Navigate to="/calendar" replace />} />
            <Route
              path="comparison"
              element={
                <MobileComparisonPage
                  prefill={effectiveComparisonPrefill}
                  onPrefillConsumed={consumeComparisonPrefill}
                />
              }
            />
            <Route path="ai-assistant" element={<MobileAiAssistantPage />} />
            <Route path="notifications" element={<MobileNotificationPage isCoach={isCoach} />} />
            <Route path="settings/*" element={<SettingsPage />} />
          </Route>
          <Route path="garmin/callback" element={<GarminCallbackPage />} />
          <Route path="*" element={<Navigate to="/calendar" replace />} />
        </Routes>}
      </ComparisonNavigationProvider>
      </NotificationCountProvider>
    </AthleteProvider>
    </PwaInstallProvider>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/splash" element={<SplashPage />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
    </BrowserRouter>
  );
}
